import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { DocManiaLogoImage } from '../components/DocManiaLogo';
import { toast } from 'sonner';
import { resolveThumbnailUrl } from '../lib/publicApi';
import { 
  FileText, Users, CreditCard, History, LogOut, Settings, 
  Coins, Loader2, Shield, DollarSign, Edit, Plus, Search, RefreshCw,
  Upload, Mail, Lock, CheckCircle, XCircle, AlertTriangle, Trash2, Layout,
  Camera, ImageIcon, Copy, Download, Package
} from 'lucide-react';
import AdminDigitalProductsPanel from '../components/admin/AdminDigitalProductsPanel';

const TEMPLATE_UPLOAD_DOCUMENT_TYPES = [
  { value: 'business_plan_funding', label: 'Business Plan & Funding' },
  { value: 'business_grant_proposal', label: 'Business Grant Proposal' },
  { value: 'ngo_grant_proposal', label: 'NGO Grant Proposal' },
  { value: 'service_proposal', label: 'Service Proposal' },
  { value: 'resume', label: 'Resume' },
  { value: 'cover_letter', label: 'Cover Letter' },
  { value: 'invoice', label: 'Invoice' },
  { value: 'quotation', label: 'Quotation' },
  { value: 'purchase_order', label: 'Purchase Order' },
  { value: 'delivery_note', label: 'Delivery Note' },
  { value: 'waybill', label: 'Waybill' },
  { value: 'proforma_invoice', label: 'Proforma Invoice' },
];

const DELETION_COLLECTION_LABELS = {
  users: 'User account',
  credit_ledger: 'Credit ledger entries',
  jobs: 'Document jobs',
  job_outputs: 'Document outputs',
  generation_jobs: 'Generation jobs',
  generation_job_outputs: 'Generation outputs',
  job_logos: 'Uploaded logos',
  analysis_jobs: 'Analysis jobs',
  registration_waitlist: 'Waitlist entries',
  contact_submissions: 'Contact submissions',
};

const formatStorageBytes = (bytes = 0) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const formatJoinedDate = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString();
};

const escapeCsvCell = (value) => {
  const text = String(value ?? '');
  const formulaSafe = /^[=+\-@]/.test(text) ? `'${text}` : text;
  return `"${formulaSafe.replace(/"/g, '""')}"`;
};

export default function AdminDashboardPage() {
  const { user, api, logout, isAdmin, isSuperAdmin } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('users');
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({
    users: [],
    ledger: [],
    payments: [],
    jobs: [],
    pricing: [],
    templates: [],
    emailStatus: null,
    serviceConfig: null,
    visaPresets: [],
    waitlist: [],
    waitlistTotal: 0,
  });
  const [stats, setStats] = useState({
    totalUsers: 0,
    totalPayments: 0,
    totalJobs: 0
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [editingUser, setEditingUser] = useState(null);
  const [editingPack, setEditingPack] = useState(null);
  const [grantCredits, setGrantCredits] = useState({ userId: '', amount: 0, description: '' });
  const [uploadLoading, setUploadLoading] = useState(false);
  const [uploadDocumentType, setUploadDocumentType] = useState('');
  const [passwordForm, setPasswordForm] = useState({ current: '', newPass: '', confirm: '' });
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [deleteUser, setDeleteUser] = useState(null);
  const [deletePreview, setDeletePreview] = useState(null);
  const [deletePreviewLoading, setDeletePreviewLoading] = useState(false);
  const [deletePreviewError, setDeletePreviewError] = useState('');
  const [deleteConfirmation, setDeleteConfirmation] = useState('');
  const [deleteReason, setDeleteReason] = useState('');
  const [deleteExecuting, setDeleteExecuting] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (!isAdmin) {
      navigate('/dashboard');
      return;
    }
    fetchAllData();
  }, [isAdmin]);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const [usersRes, ledgerRes, paymentsRes, jobsRes, pricingRes, templatesRes, emailRes, serviceConfigRes, presetsRes, waitlistRes] = await Promise.all([
        api.get('/admin/users?limit=100'),
        api.get('/admin/ledger?limit=100'),
        api.get('/admin/payments?limit=100'),
        api.get('/admin/jobs?limit=100'),
        api.get('/admin/pricing'),
        api.get('/admin/templates'),
        api.get('/admin/email-status'),
        api.get('/admin/service-config'),
        api.get('/admin/visa-presets'),
        api.get('/admin/waitlist?limit=50000'),
      ]);
      
      setData({
        users: usersRes.data.users,
        ledger: ledgerRes.data.entries,
        payments: paymentsRes.data.payments,
        jobs: jobsRes.data.jobs,
        pricing: pricingRes.data.packs,
        templates: templatesRes.data.templates,
        emailStatus: emailRes.data,
        serviceConfig: serviceConfigRes.data.services,
        visaPresets: presetsRes.data.presets,
        waitlist: waitlistRes.data.entries,
        waitlistTotal: waitlistRes.data.total,
      });
      
      setStats({
        totalUsers: usersRes.data.total,
        totalPayments: paymentsRes.data.total,
        totalJobs: jobsRes.data.total
      });
    } catch (error) {
      toast.error('Failed to load admin data');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const handleUpdateUser = async () => {
    if (!editingUser) return;
    try {
      await api.patch(`/admin/users/${editingUser.id}`, {
        is_active: editingUser.is_active,
        role: editingUser.role,
        pricing_region: editingUser.pricing_region
      });
      toast.success('User updated successfully');
      setEditingUser(null);
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update user');
    }
  };

  const resetDeleteDialog = () => {
    setDeleteUser(null);
    setDeletePreview(null);
    setDeletePreviewError('');
    setDeleteConfirmation('');
    setDeleteReason('');
  };

  const handleDeleteDialogChange = (open) => {
    if (!open && !deleteExecuting) resetDeleteDialog();
  };

  const handleOpenDelete = async (targetUser) => {
    setDeleteUser(targetUser);
    setDeletePreview(null);
    setDeletePreviewError('');
    setDeleteConfirmation('');
    setDeleteReason('');
    setDeletePreviewLoading(true);
    try {
      const response = await api.post(`/admin/users/${targetUser.id}/delete-preview`);
      setDeletePreview(response.data);
    } catch (error) {
      setDeletePreviewError(error.response?.data?.detail || 'Failed to load deletion preview');
    } finally {
      setDeletePreviewLoading(false);
    }
  };

  const handleDeleteUser = async () => {
    if (!deleteUser || !deletePreview) return;
    setDeleteExecuting(true);
    try {
      await api.post(`/admin/users/${deleteUser.id}/delete`, {
        confirm_email: deleteConfirmation.trim(),
        reason: deleteReason.trim() || null,
      });
      toast.success(`${deleteUser.email} was permanently deleted`);
      resetDeleteDialog();
      await fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'User deletion failed');
    } finally {
      setDeleteExecuting(false);
    }
  };

  const handleCopyWaitlistEmails = async () => {
    const emails = data.waitlist.map((entry) => entry.email).filter(Boolean);
    if (!emails.length) {
      toast.error('There are no waitlist emails to copy');
      return;
    }

    const text = emails.join('\n');
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        const textarea = document.createElement('textarea');
        textarea.value = text;
        textarea.style.position = 'fixed';
        textarea.style.opacity = '0';
        document.body.appendChild(textarea);
        textarea.select();
        const copied = document.execCommand('copy');
        textarea.remove();
        if (!copied) throw new Error('Copy command failed');
      }
      toast.success(`Copied ${emails.length} waitlist email${emails.length === 1 ? '' : 's'}`);
    } catch (error) {
      toast.error('Could not copy waitlist emails');
    }
  };

  const handleExportWaitlist = () => {
    if (!data.waitlist.length) {
      toast.error('There are no waitlist entries to export');
      return;
    }

    const rows = [
      ['email', 'country', 'date_joined'],
      ...data.waitlist.map((entry) => [entry.email, entry.country, entry.created_at]),
    ];
    const csv = rows.map((row) => row.map(escapeCsvCell).join(',')).join('\r\n');
    const blob = new Blob([`\uFEFF${csv}`], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `docmania-registration-waitlist-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  };

  const handleUpdatePricing = async () => {
    if (!editingPack) return;
    try {
      await api.patch(`/admin/pricing/${editingPack.id}`, {
        name: editingPack.name,
        credits: editingPack.credits,
        price_ngn: editingPack.price_ngn,
        price_usd: editingPack.price_usd,
        is_active: editingPack.is_active
      });
      toast.success('Pricing pack updated');
      setEditingPack(null);
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update pricing');
    }
  };

  const handleGrantCredits = async () => {
    if (!grantCredits.userId || !grantCredits.amount) {
      toast.error('Please fill in all fields');
      return;
    }
    try {
      await api.post(`/admin/grant-credits?user_id=${grantCredits.userId}&amount=${grantCredits.amount}&description=${encodeURIComponent(grantCredits.description)}`);
      toast.success('Credits granted successfully');
      setGrantCredits({ userId: '', amount: 0, description: '' });
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to grant credits');
    }
  };

  const handleUploadZip = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!uploadDocumentType) {
      toast.error('Please select a document type before uploading.');
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }
    if (!file.name.toLowerCase().endsWith('.zip')) {
      toast.error('Only ZIP files are allowed');
      return;
    }
    setUploadLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('document_type', uploadDocumentType);
      const res = await api.post('/admin/templates/upload-zip', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      const d = res.data;
      toast.success(`Uploaded ${d.total_created} template(s)${d.total_skipped > 0 ? `, ${d.total_skipped} skipped` : ''}`);
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload ZIP');
    } finally {
      setUploadLoading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleTogglePreset = async (presetId, currentActive) => {
    try {
      await api.patch(`/admin/visa-presets/${presetId}`, { is_active: !currentActive });
      toast.success(`Preset ${!currentActive ? 'enabled' : 'disabled'}`);
      setData(prev => ({
        ...prev,
        visaPresets: prev.visaPresets.map(p => p.id === presetId ? { ...p, is_active: !currentActive } : p)
      }));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update preset');
    }
  };

  const handleToggleTemplate = async (templateId, currentActive) => {
    try {
      await api.patch(`/admin/templates/${templateId}`, { is_active: !currentActive });
      toast.success(`Template ${!currentActive ? 'enabled' : 'disabled'}`);
      setData(prev => ({
        ...prev,
        templates: prev.templates.map(t => t.id === templateId ? { ...t, is_active: !currentActive } : t)
      }));
    } catch (error) {
      toast.error('Failed to update template');
    }
  };

  const handleDeleteTemplate = async (templateId) => {
    try {
      await api.delete(`/admin/templates/${templateId}`);
      toast.success('Template deleted');
      setData(prev => ({
        ...prev,
        templates: prev.templates.filter(t => t.id !== templateId)
      }));
    } catch (error) {
      toast.error('Failed to delete template');
    }
  };

  const handleUploadThumbnail = async (templateId, file) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      await api.post(`/admin/templates/${templateId}/thumbnail`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Thumbnail uploaded');
      fetchAllData();
    } catch (error) {
      toast.error('Failed to upload thumbnail');
    }
  };

  const handleRegenerateThumbnail = async (templateId) => {
    try {
      await api.post(`/admin/templates/${templateId}/regenerate-thumbnail`);
      toast.success('Thumbnail regenerated');
      fetchAllData();
    } catch (error) {
      toast.error('Failed to regenerate thumbnail');
    }
  };

  const handleRemoveThumbnail = async (templateId) => {
    try {
      await api.delete(`/admin/templates/${templateId}/thumbnail`);
      toast.success('Thumbnail removed');
      fetchAllData();
    } catch (error) {
      toast.error('Failed to remove thumbnail');
    }
  };

  const handleChangePassword = async () => {
    if (!passwordForm.current || !passwordForm.newPass) {
      toast.error('Please fill in all fields');
      return;
    }
    if (passwordForm.newPass !== passwordForm.confirm) {
      toast.error('New passwords do not match');
      return;
    }
    if (passwordForm.newPass.length < 6) {
      toast.error('New password must be at least 6 characters');
      return;
    }
    setPasswordLoading(true);
    try {
      await api.post('/auth/change-password', {
        current_password: passwordForm.current,
        new_password: passwordForm.newPass
      });
      toast.success('Password changed successfully');
      setPasswordForm({ current: '', newPass: '', confirm: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change password');
    } finally {
      setPasswordLoading(false);
    }
  };

  const getRoleBadge = (role) => {
    const colors = {
      super_admin: 'bg-primary/10 text-primary',
      admin: 'bg-primary/10 text-primary',
      reviewer: 'bg-primary/10 text-primary',
      user: 'bg-muted text-muted-foreground'
    };
    return <Badge className={`${colors[role] || colors.user} border-0`}>{role}</Badge>;
  };

  const getStatusBadge = (status) => {
    const colors = {
      success: 'bg-primary/10 text-primary',
      pending: 'bg-muted text-muted-foreground',
      failed: 'bg-destructive/10 text-destructive',
      completed: 'bg-primary/10 text-primary',
      processing: 'bg-primary/10 text-primary',
      queued: 'bg-muted text-muted-foreground'
    };
    return <Badge className={`${colors[status] || colors.pending} border-0`}>{status}</Badge>;
  };

  const filteredUsers = data.users.filter(u => 
    u.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    u.full_name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const templatesByType = data.templates.reduce((acc, t) => {
    const key = t.document_type || 'unknown';
    if (!acc[key]) acc[key] = [];
    acc[key].push(t);
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-muted/20">
      {/* Sidebar */}
      <aside className="fixed left-0 top-0 bottom-0 w-64 border-r border-border bg-white p-4 hidden lg:block">
        <div className="flex items-center gap-2 mb-8">
          <DocManiaLogoImage size={36} />
          <span className="text-xl font-bold text-foreground">Admin Panel</span>
        </div>
        
        <nav className="space-y-1">
          {[
            { id: 'users', icon: Users, label: 'Users' },
            { id: 'waitlist', icon: Mail, label: 'Waitlist' },
            { id: 'templates', icon: Layout, label: 'Templates' },
            { id: 'digital-products', icon: Package, label: 'Digital Products' },
            { id: 'ledger', icon: Coins, label: 'Credit Ledger' },
            { id: 'payments', icon: CreditCard, label: 'Payments' },
            { id: 'jobs', icon: History, label: 'Jobs' },
            { id: 'pricing', icon: DollarSign, label: 'Pricing' },
            { id: 'email', icon: Mail, label: 'Email Config' },
            { id: 'services-config', icon: Shield, label: 'Services' },
            { id: 'visa-presets', icon: Camera, label: 'Visa Presets' },
            { id: 'settings', icon: Settings, label: 'Settings' },
          ].map(item => (
            <Button 
              key={item.id}
              variant={activeTab === item.id ? 'secondary' : 'ghost'} 
              className="w-full justify-start"
              onClick={() => setActiveTab(item.id)}
              data-testid={`admin-nav-${item.id}`}
            >
              <item.icon className="mr-2 h-4 w-4" />
              {item.label}
            </Button>
          ))}
        </nav>
        
        <div className="absolute bottom-4 left-4 right-4 space-y-2">
          <Link to="/dashboard">
            <Button variant="outline" className="w-full justify-start">
              <FileText className="mr-2 h-4 w-4" />
              User Dashboard
            </Button>
          </Link>
          <Button variant="ghost" className="w-full justify-start text-muted-foreground" onClick={handleLogout}>
            <LogOut className="mr-2 h-4 w-4" />
            Logout
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="lg:ml-64 p-4 lg:p-8">
        <div className="max-w-7xl mx-auto space-y-8">
          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <h1 className="text-2xl font-bold text-foreground">Admin Dashboard</h1>
              <p className="text-muted-foreground">Manage users, templates, credits, and more</p>
            </div>
            <Button onClick={fetchAllData} variant="outline" data-testid="refresh-btn">
              <RefreshCw className="mr-2 h-4 w-4" />
              Refresh
            </Button>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Card className="card-elevated" data-testid="total-users-card">
              <CardHeader className="pb-2">
                <CardDescription className="flex items-center gap-2">
                  <Users className="h-4 w-4" />
                  Total Users
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-foreground">{stats.totalUsers}</div>
              </CardContent>
            </Card>
            <Card className="card-elevated" data-testid="total-payments-card">
              <CardHeader className="pb-2">
                <CardDescription className="flex items-center gap-2">
                  <CreditCard className="h-4 w-4" />
                  Total Payments
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-foreground">{stats.totalPayments}</div>
              </CardContent>
            </Card>
            <Card className="card-elevated" data-testid="total-jobs-card">
              <CardHeader className="pb-2">
                <CardDescription className="flex items-center gap-2">
                  <History className="h-4 w-4" />
                  Total Jobs
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-foreground">{stats.totalJobs}</div>
              </CardContent>
            </Card>
          </div>

          {/* ============== USERS TAB ============== */}
          {activeTab === 'users' && (
            <Card className="card-elevated" data-testid="users-section">
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle className="text-foreground">Users</CardTitle>
                  <CardDescription>Manage user accounts and roles</CardDescription>
                </div>
                <div className="flex items-center gap-2">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input 
                      placeholder="Search users..." 
                      className="pl-10 w-64"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      data-testid="user-search-input"
                    />
                  </div>
                  <Dialog>
                    <DialogTrigger asChild>
                      <Button data-testid="grant-credits-btn">
                        <Plus className="mr-2 h-4 w-4" />
                        Grant Credits
                      </Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle>Grant Credits</DialogTitle>
                        <DialogDescription>Add credits to a user's account</DialogDescription>
                      </DialogHeader>
                      <div className="space-y-4">
                        <div>
                          <Label>User</Label>
                          <Select value={grantCredits.userId} onValueChange={(v) => setGrantCredits({...grantCredits, userId: v})}>
                            <SelectTrigger data-testid="grant-user-select">
                              <SelectValue placeholder="Select user" />
                            </SelectTrigger>
                            <SelectContent>
                              {data.users.map((u) => (
                                <SelectItem key={u.id} value={u.id}>{u.email}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label>Amount</Label>
                          <Input 
                            type="number" 
                            value={grantCredits.amount}
                            onChange={(e) => setGrantCredits({...grantCredits, amount: parseInt(e.target.value) || 0})}
                            data-testid="grant-amount-input"
                          />
                        </div>
                        <div>
                          <Label>Description</Label>
                          <Input 
                            value={grantCredits.description}
                            onChange={(e) => setGrantCredits({...grantCredits, description: e.target.value})}
                            placeholder="Reason for granting credits"
                            data-testid="grant-description-input"
                          />
                        </div>
                        <Button onClick={handleGrantCredits} className="w-full" data-testid="confirm-grant-btn">
                          Grant Credits
                        </Button>
                      </div>
                    </DialogContent>
                  </Dialog>
                </div>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Email</TableHead>
                      <TableHead>Name</TableHead>
                      <TableHead>Role</TableHead>
                      <TableHead>Country</TableHead>
                      <TableHead>Pricing Region</TableHead>
                      <TableHead>Balance</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredUsers.map((u) => (
                      <TableRow key={u.id} data-testid={`user-row-${u.id}`}>
                        <TableCell className="font-medium text-foreground">{u.email}</TableCell>
                        <TableCell className="text-foreground">{u.full_name}</TableCell>
                        <TableCell>{getRoleBadge(u.role)}</TableCell>
                        <TableCell className="text-muted-foreground">{u.country}</TableCell>
                        <TableCell className="text-muted-foreground">{u.pricing_region}</TableCell>
                        <TableCell className="text-foreground">{u.credit_balance}</TableCell>
                        <TableCell>
                          {u.is_active ? (
                            <Badge className="bg-primary/10 text-primary border-0">Active</Badge>
                          ) : (
                            <Badge className="bg-destructive/10 text-destructive border-0">Disabled</Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            <Dialog>
                              <DialogTrigger asChild>
                                <Button variant="ghost" size="sm" onClick={() => setEditingUser({...u})} data-testid={`edit-user-${u.id}`}>
                                  <Edit className="h-4 w-4" />
                                </Button>
                              </DialogTrigger>
                              <DialogContent>
                                <DialogHeader>
                                  <DialogTitle>Edit User</DialogTitle>
                                  <DialogDescription>{editingUser?.email}</DialogDescription>
                                </DialogHeader>
                                {editingUser && (
                                  <div className="space-y-4">
                                    <div>
                                      <Label>Role</Label>
                                      <Select value={editingUser.role} onValueChange={(v) => setEditingUser({...editingUser, role: v})}>
                                        <SelectTrigger data-testid="edit-role-select">
                                          <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                          <SelectItem value="user">User</SelectItem>
                                          <SelectItem value="reviewer">Reviewer</SelectItem>
                                          <SelectItem value="admin">Admin</SelectItem>
                                          {isSuperAdmin && <SelectItem value="super_admin">Super Admin</SelectItem>}
                                        </SelectContent>
                                      </Select>
                                    </div>
                                    <div>
                                      <Label>Pricing Region</Label>
                                      <Select
                                        value={editingUser.pricing_region || ''}
                                        onValueChange={(v) => setEditingUser({...editingUser, pricing_region: v})}
                                      >
                                        <SelectTrigger data-testid="edit-pricing-region-select">
                                          <SelectValue placeholder="Select pricing region" />
                                        </SelectTrigger>
                                        <SelectContent>
                                          <SelectItem value="PAYSTACK">Local (NGN)</SelectItem>
                                          <SelectItem value="INTERNATIONAL">International</SelectItem>
                                        </SelectContent>
                                      </Select>
                                    </div>
                                    <div className="flex items-center justify-between">
                                      <Label>Active</Label>
                                      <Switch
                                        checked={editingUser.is_active}
                                        onCheckedChange={(v) => setEditingUser({...editingUser, is_active: v})}
                                        data-testid="edit-active-switch"
                                      />
                                    </div>
                                    <Button onClick={handleUpdateUser} className="w-full" data-testid="save-user-btn">
                                      Save Changes
                                    </Button>
                                  </div>
                                )}
                              </DialogContent>
                            </Dialog>
                            {isSuperAdmin && u.id !== user?.id && u.role !== 'super_admin' && (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-destructive hover:bg-destructive/10 hover:text-destructive"
                                onClick={() => handleOpenDelete(u)}
                                aria-label={`Delete ${u.email}`}
                                title="Preview permanent deletion"
                                data-testid={`delete-user-${u.id}`}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
              <Dialog open={Boolean(deleteUser)} onOpenChange={handleDeleteDialogChange}>
                <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle className="flex items-center gap-2 text-destructive">
                      <AlertTriangle className="h-5 w-5" />
                      Permanently delete user
                    </DialogTitle>
                    <DialogDescription>
                      This cannot be undone. Review the dry-run before confirming deletion of{' '}
                      <span className="font-medium text-foreground">{deleteUser?.email}</span>.
                    </DialogDescription>
                  </DialogHeader>

                  {deletePreviewLoading && (
                    <div className="flex items-center justify-center gap-2 py-10 text-muted-foreground">
                      <Loader2 className="h-5 w-5 animate-spin" />
                      Loading deletion preview…
                    </div>
                  )}

                  {deletePreviewError && (
                    <div className="rounded-md border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
                      {deletePreviewError}
                    </div>
                  )}

                  {deletePreview && (
                    <div className="space-y-5">
                      {deletePreview.blockers?.length > 0 && (
                        <div className="rounded-md border border-destructive/30 bg-destructive/10 p-4">
                          <p className="mb-2 font-medium text-destructive">Deletion is blocked</p>
                          <ul className="list-disc space-y-1 pl-5 text-sm text-destructive">
                            {deletePreview.blockers.map((blocker) => (
                              <li key={blocker}>{blocker}</li>
                            ))}
                          </ul>
                        </div>
                      )}

                      <div>
                        <h3 className="mb-2 text-sm font-semibold text-foreground">Data to be permanently deleted</h3>
                        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                          {Object.entries(deletePreview.planned_deletes || {}).map(([collection, count]) => (
                            <div key={collection} className="flex items-center justify-between rounded-md border px-3 py-2 text-sm">
                              <span className="text-muted-foreground">
                                {DELETION_COLLECTION_LABELS[collection] || collection}
                              </span>
                              <span className="font-semibold text-foreground">{count}</span>
                            </div>
                          ))}
                        </div>
                      </div>

                      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                        <div className="rounded-md border p-3 text-sm">
                          <p className="font-medium text-foreground">Object storage</p>
                          <p className="mt-1 text-muted-foreground">
                            {deletePreview.s3?.objects || 0} object(s),{' '}
                            {formatStorageBytes(deletePreview.s3?.bytes || 0)}
                          </p>
                        </div>
                        <div className="rounded-md border p-3 text-sm">
                          <p className="font-medium text-foreground">Payments retained</p>
                          <p className="mt-1 text-muted-foreground">
                            {deletePreview.anonymize?.payments || 0} payment record(s) will be anonymized.
                          </p>
                        </div>
                      </div>

                      {deletePreview.warnings?.length > 0 && (
                        <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
                          <ul className="list-disc space-y-1 pl-5">
                            {deletePreview.warnings.map((warning) => (
                              <li key={warning}>{warning}</li>
                            ))}
                          </ul>
                        </div>
                      )}

                      <div className="space-y-2">
                        <Label htmlFor="delete-reason">Reason (optional)</Label>
                        <Input
                          id="delete-reason"
                          value={deleteReason}
                          onChange={(event) => setDeleteReason(event.target.value)}
                          placeholder="Reason retained in the admin audit log"
                          disabled={deleteExecuting}
                          data-testid="delete-user-reason"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="delete-confirmation">
                          Type <span className="font-semibold">{deleteUser?.email}</span> to confirm
                        </Label>
                        <Input
                          id="delete-confirmation"
                          value={deleteConfirmation}
                          onChange={(event) => setDeleteConfirmation(event.target.value)}
                          placeholder={deleteUser?.email}
                          autoComplete="off"
                          disabled={deleteExecuting || deletePreview.blockers?.length > 0}
                          data-testid="delete-user-confirmation"
                        />
                      </div>
                    </div>
                  )}

                  <DialogFooter>
                    <Button
                      variant="outline"
                      onClick={resetDeleteDialog}
                      disabled={deleteExecuting}
                    >
                      Cancel
                    </Button>
                    <Button
                      variant="destructive"
                      onClick={handleDeleteUser}
                      disabled={
                        deleteExecuting ||
                        !deletePreview ||
                        deletePreview.blockers?.length > 0 ||
                        deleteConfirmation.trim().toLowerCase() !== deleteUser?.email?.toLowerCase()
                      }
                      data-testid="confirm-delete-user"
                    >
                      {deleteExecuting ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Deleting…
                        </>
                      ) : (
                        <>
                          <Trash2 className="mr-2 h-4 w-4" />
                          Permanently delete
                        </>
                      )}
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </Card>
          )}

          {/* ============== WAITLIST TAB ============== */}
          {activeTab === 'waitlist' && (
            <Card className="card-elevated" data-testid="waitlist-section">
              <CardHeader className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <CardTitle className="text-foreground">Registration Waitlist</CardTitle>
                  <CardDescription>
                    {data.waitlistTotal} email{data.waitlistTotal === 1 ? '' : 's'} waiting for international registration
                  </CardDescription>
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button
                    variant="outline"
                    onClick={handleCopyWaitlistEmails}
                    disabled={!data.waitlist.length}
                    data-testid="copy-waitlist-emails"
                  >
                    <Copy className="mr-2 h-4 w-4" />
                    Copy emails
                  </Button>
                  <Button
                    onClick={handleExportWaitlist}
                    disabled={!data.waitlist.length}
                    data-testid="export-waitlist-csv"
                  >
                    <Download className="mr-2 h-4 w-4" />
                    Export CSV
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {data.waitlist.length ? (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Email</TableHead>
                          <TableHead>Country</TableHead>
                          <TableHead>Date joined</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {data.waitlist.map((entry) => (
                          <TableRow key={entry.email}>
                            <TableCell className="font-medium text-foreground">{entry.email}</TableCell>
                            <TableCell className="text-muted-foreground">{entry.country || '—'}</TableCell>
                            <TableCell className="text-muted-foreground">
                              {formatJoinedDate(entry.created_at)}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                ) : (
                  <div className="py-12 text-center text-sm text-muted-foreground">
                    No registration waitlist entries yet.
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* ============== DIGITAL PRODUCTS TAB ============== */}
          {activeTab === 'digital-products' && (
            <div data-testid="digital-products-section">
              <AdminDigitalProductsPanel />
            </div>
          )}

          {/* ============== TEMPLATES TAB ============== */}
          {activeTab === 'templates' && (
            <div className="space-y-6" data-testid="templates-section">
              <Card className="card-elevated">
                <CardHeader>
                  <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                    <div>
                      <CardTitle className="text-foreground">Template Management</CardTitle>
                      <CardDescription>Upload and manage document templates</CardDescription>
                    </div>
                    <div className="flex w-full flex-col gap-3 sm:w-auto sm:min-w-[280px]">
                      <div className="space-y-1.5">
                        <Label htmlFor="template-document-type">
                          Document Type / Category <span className="text-destructive">*</span>
                        </Label>
                        <Select
                          value={uploadDocumentType}
                          onValueChange={setUploadDocumentType}
                        >
                          <SelectTrigger id="template-document-type" data-testid="template-document-type-select">
                            <SelectValue placeholder="Select document type" />
                          </SelectTrigger>
                          <SelectContent>
                            {TEMPLATE_UPLOAD_DOCUMENT_TYPES.map((opt) => (
                              <SelectItem key={opt.value} value={opt.value}>
                                {opt.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept=".zip"
                        className="hidden"
                        onChange={handleUploadZip}
                        data-testid="template-zip-input"
                      />
                      <Button
                        onClick={() => {
                          if (!uploadDocumentType) {
                            toast.error('Please select a document type before uploading.');
                            return;
                          }
                          fileInputRef.current?.click();
                        }}
                        disabled={uploadLoading || !uploadDocumentType}
                        data-testid="upload-zip-btn"
                        className="w-full sm:w-auto"
                      >
                        {uploadLoading ? (
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        ) : (
                          <Upload className="mr-2 h-4 w-4" />
                        )}
                        {uploadLoading ? 'Uploading...' : 'Upload ZIP'}
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="p-4 rounded-lg bg-muted/50 text-sm text-muted-foreground mb-6">
                    <p className="font-medium text-foreground mb-1">ZIP upload</p>
                    <p className="mb-2">Select a document type above, then upload a ZIP. The selected type is applied to every template in the archive.</p>
                    <p>If you leave the type unset on the server, each top-level folder name can still auto-map to a category (Resume, Invoice, Quotation, etc.).</p>
                  </div>

                  {data.templates.length === 0 ? (
                    <div className="text-center py-12">
                      <Layout className="h-10 w-10 mx-auto text-muted-foreground/50 mb-3" />
                      <p className="text-muted-foreground">No templates uploaded yet.</p>
                      <p className="text-sm text-muted-foreground">Upload a ZIP file to get started.</p>
                    </div>
                  ) : (
                    <div className="space-y-6">
                      {Object.entries(templatesByType).map(([docType, templates]) => (
                        <div key={docType}>
                          <h3 className="text-sm font-semibold text-foreground mb-3 uppercase tracking-wider">
                            {docType.replace(/_/g, ' ')} ({templates.length})
                          </h3>
                          <Table>
                            <TableHeader>
                              <TableRow>
                                <TableHead className="w-16">Thumb</TableHead>
                                <TableHead>Name</TableHead>
                                <TableHead>Filename</TableHead>
                                <TableHead>Uploaded</TableHead>
                                <TableHead>Status</TableHead>
                                <TableHead>Actions</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {templates.map((t) => (
                                <TableRow key={t.id} data-testid={`template-row-${t.id}`}>
                                  <TableCell>
                                    {t.thumbnail_signed_url ? (
                                      <img
                                        src={resolveThumbnailUrl(t.thumbnail_signed_url)}
                                        alt={t.name}
                                        className="w-10 h-14 object-cover rounded border border-border"
                                        data-testid={`template-thumb-${t.id}`}
                                      />
                                    ) : (
                                      <div className="w-10 h-14 rounded border border-dashed border-border flex items-center justify-center bg-muted/30">
                                        <FileText className="h-4 w-4 text-muted-foreground/40" />
                                      </div>
                                    )}
                                  </TableCell>
                                  <TableCell>
                                    <div>
                                      <p className="font-medium text-foreground">{t.name}</p>
                                      {t.thumbnail_source && (
                                        <Badge variant="outline" className="text-[10px] mt-0.5">
                                          {t.thumbnail_source} thumb
                                        </Badge>
                                      )}
                                    </div>
                                  </TableCell>
                                  <TableCell className="font-mono text-xs text-muted-foreground">{t.filename}</TableCell>
                                  <TableCell className="text-muted-foreground text-sm">
                                    {new Date(t.created_at).toLocaleDateString()}
                                  </TableCell>
                                  <TableCell>
                                    <div className="flex items-center gap-2">
                                      <Switch
                                        checked={t.is_active}
                                        onCheckedChange={() => handleToggleTemplate(t.id, t.is_active)}
                                        data-testid={`toggle-template-${t.id}`}
                                      />
                                      <span className="text-xs text-muted-foreground">
                                        {t.is_active ? 'Enabled' : 'Disabled'}
                                      </span>
                                    </div>
                                  </TableCell>
                                  <TableCell>
                                    <div className="flex items-center gap-1">
                                      <label className="cursor-pointer" title="Upload manual thumbnail">
                                        <input
                                          type="file"
                                          accept=".png,.jpg,.jpeg"
                                          className="hidden"
                                          onChange={(e) => {
                                            if (e.target.files?.[0]) handleUploadThumbnail(t.id, e.target.files[0]);
                                            e.target.value = '';
                                          }}
                                          data-testid={`upload-thumb-${t.id}`}
                                        />
                                        <div className="inline-flex items-center justify-center h-8 w-8 rounded-md hover:bg-muted transition-colors">
                                          <ImageIcon className="h-4 w-4 text-muted-foreground" />
                                        </div>
                                      </label>
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        className="h-8 w-8 p-0"
                                        title="Regenerate auto thumbnail"
                                        onClick={() => handleRegenerateThumbnail(t.id)}
                                        data-testid={`regen-thumb-${t.id}`}
                                      >
                                        <RefreshCw className="h-4 w-4 text-muted-foreground" />
                                      </Button>
                                      {t.thumbnail_url && (
                                        <Button
                                          variant="ghost"
                                          size="sm"
                                          className="h-8 w-8 p-0"
                                          title="Remove thumbnail"
                                          onClick={() => handleRemoveThumbnail(t.id)}
                                          data-testid={`remove-thumb-${t.id}`}
                                        >
                                          <XCircle className="h-4 w-4 text-muted-foreground" />
                                        </Button>
                                      )}
                                      <Button 
                                        variant="ghost" 
                                        size="sm" 
                                        className="h-8 w-8 p-0 text-destructive hover:text-destructive"
                                        onClick={() => handleDeleteTemplate(t.id)}
                                        data-testid={`delete-template-${t.id}`}
                                      >
                                        <Trash2 className="h-4 w-4" />
                                      </Button>
                                    </div>
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          )}

          {/* ============== LEDGER TAB ============== */}
          {activeTab === 'ledger' && (
            <Card className="card-elevated" data-testid="ledger-section">
              <CardHeader>
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <CardTitle className="text-foreground">Credit Ledger</CardTitle>
                    <CardDescription>Immutable record of all credit transactions</CardDescription>
                  </div>
                  <Dialog>
                    <DialogTrigger asChild>
                      <Button data-testid="ledger-grant-credits-btn">
                        <Plus className="mr-2 h-4 w-4" />
                        Grant Credits
                      </Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle>Grant Credits</DialogTitle>
                        <DialogDescription>Add credits to a user's account</DialogDescription>
                      </DialogHeader>
                      <div className="space-y-4">
                        <div>
                          <Label>User</Label>
                          <Select value={grantCredits.userId} onValueChange={(v) => setGrantCredits({...grantCredits, userId: v})}>
                            <SelectTrigger data-testid="ledger-grant-user-select">
                              <SelectValue placeholder="Select user" />
                            </SelectTrigger>
                            <SelectContent>
                              {data.users.map((u) => (
                                <SelectItem key={u.id} value={u.id}>{u.email}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label>Amount</Label>
                          <Input
                            type="number"
                            value={grantCredits.amount}
                            onChange={(e) => setGrantCredits({...grantCredits, amount: parseInt(e.target.value) || 0})}
                            data-testid="ledger-grant-amount-input"
                          />
                        </div>
                        <div>
                          <Label>Description</Label>
                          <Input
                            value={grantCredits.description}
                            onChange={(e) => setGrantCredits({...grantCredits, description: e.target.value})}
                            placeholder="Reason for granting credits"
                            data-testid="ledger-grant-description-input"
                          />
                        </div>
                        <Button onClick={handleGrantCredits} className="w-full" data-testid="ledger-confirm-grant-btn">
                          Grant Credits
                        </Button>
                      </div>
                    </DialogContent>
                  </Dialog>
                </div>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date</TableHead>
                      <TableHead>User ID</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Amount</TableHead>
                      <TableHead>Balance After</TableHead>
                      <TableHead>Description</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data.ledger.map((entry) => (
                      <TableRow key={entry.id}>
                        <TableCell className="text-foreground">{new Date(entry.created_at).toLocaleString()}</TableCell>
                        <TableCell className="font-mono text-xs text-muted-foreground">{entry.user_id.slice(0, 8)}...</TableCell>
                        <TableCell>
                          <Badge variant="secondary" className="border-0">{entry.transaction_type}</Badge>
                        </TableCell>
                        <TableCell className={entry.amount >= 0 ? 'text-primary font-medium' : 'text-destructive font-medium'}>
                          {entry.amount >= 0 ? '+' : ''}{entry.amount}
                        </TableCell>
                        <TableCell className="text-foreground">{entry.balance_after}</TableCell>
                        <TableCell className="text-sm text-muted-foreground">{entry.description}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}

          {/* ============== PAYMENTS TAB ============== */}
          {activeTab === 'payments' && (
            <Card className="card-elevated" data-testid="payments-section">
              <CardHeader>
                <CardTitle className="text-foreground">Payments</CardTitle>
                <CardDescription>All payment transactions</CardDescription>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date</TableHead>
                      <TableHead>Reference</TableHead>
                      <TableHead>User ID</TableHead>
                      <TableHead>Amount</TableHead>
                      <TableHead>Credits</TableHead>
                      <TableHead>Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data.payments.map((payment) => (
                      <TableRow key={payment.id}>
                        <TableCell className="text-foreground">{new Date(payment.created_at).toLocaleString()}</TableCell>
                        <TableCell className="font-mono text-xs text-muted-foreground">{payment.reference}</TableCell>
                        <TableCell className="font-mono text-xs text-muted-foreground">{payment.user_id.slice(0, 8)}...</TableCell>
                        <TableCell className="text-foreground">
                          {payment.currency === 'NGN' ? '\u20A6' : '$'}{(payment.amount / 100).toLocaleString()}
                        </TableCell>
                        <TableCell className="text-foreground">{payment.credits}</TableCell>
                        <TableCell>{getStatusBadge(payment.status)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}

          {/* ============== JOBS TAB (ENHANCED) ============== */}
          {activeTab === 'jobs' && (
            <Card className="card-elevated" data-testid="jobs-section">
              <CardHeader>
                <CardTitle className="text-foreground">Jobs</CardTitle>
                <CardDescription>All document processing jobs with status and errors</CardDescription>
              </CardHeader>
              <CardContent>
                {data.jobs.length === 0 ? (
                  <div className="text-center py-12">
                    <History className="h-10 w-10 mx-auto text-muted-foreground/50 mb-3" />
                    <p className="text-muted-foreground">No jobs have been processed yet.</p>
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Created</TableHead>
                        <TableHead>Updated</TableHead>
                        <TableHead>User ID</TableHead>
                        <TableHead>Service</TableHead>
                        <TableHead>Credits</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Error</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {data.jobs.map((job) => (
                        <TableRow key={job.id} data-testid={`job-row-${job.id}`}>
                          <TableCell className="text-foreground text-sm">{new Date(job.created_at).toLocaleString()}</TableCell>
                          <TableCell className="text-muted-foreground text-sm">{new Date(job.updated_at).toLocaleString()}</TableCell>
                          <TableCell className="font-mono text-xs text-muted-foreground">{job.user_id.slice(0, 8)}...</TableCell>
                          <TableCell className="text-foreground">{job.service_type.replace(/_/g, ' ')}</TableCell>
                          <TableCell className="text-foreground">{job.credits_reserved}</TableCell>
                          <TableCell>{getStatusBadge(job.status)}</TableCell>
                          <TableCell className="text-sm text-destructive max-w-[200px] truncate">
                            {job.error_message || '-'}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          )}

          {/* ============== PRICING TAB ============== */}
          {activeTab === 'pricing' && (
            <Card className="card-elevated" data-testid="pricing-section">
              <CardHeader>
                <CardTitle className="text-foreground">Pricing Packs</CardTitle>
                <CardDescription>Manage credit pricing</CardDescription>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Credits</TableHead>
                      <TableHead>Price (NGN)</TableHead>
                      <TableHead>Price (USD)</TableHead>
                      <TableHead>Region</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data.pricing.map((pack) => (
                      <TableRow key={pack.id}>
                        <TableCell className="font-medium text-foreground">{pack.name}</TableCell>
                        <TableCell className="text-foreground">{pack.credits}</TableCell>
                        <TableCell className="text-foreground">{'\u20A6'}{pack.price_ngn.toLocaleString()}</TableCell>
                        <TableCell className="text-foreground">${pack.price_usd}</TableCell>
                        <TableCell>
                          <Badge variant="outline">{pack.region}</Badge>
                        </TableCell>
                        <TableCell>
                          {pack.is_active ? (
                            <Badge className="bg-primary/10 text-primary border-0">Active</Badge>
                          ) : (
                            <Badge className="bg-destructive/10 text-destructive border-0">Inactive</Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          <Dialog>
                            <DialogTrigger asChild>
                              <Button variant="ghost" size="sm" onClick={() => setEditingPack({...pack})} data-testid={`edit-pack-${pack.id}`}>
                                <Edit className="h-4 w-4" />
                              </Button>
                            </DialogTrigger>
                            <DialogContent>
                              <DialogHeader>
                                <DialogTitle>Edit Pricing Pack</DialogTitle>
                              </DialogHeader>
                              {editingPack && (
                                <div className="space-y-4">
                                  <div>
                                    <Label>Name</Label>
                                    <Input 
                                      value={editingPack.name}
                                      onChange={(e) => setEditingPack({...editingPack, name: e.target.value})}
                                      data-testid="edit-pack-name"
                                    />
                                  </div>
                                  <div>
                                    <Label>Credits</Label>
                                    <Input 
                                      type="number"
                                      value={editingPack.credits}
                                      onChange={(e) => setEditingPack({...editingPack, credits: parseInt(e.target.value) || 0})}
                                      data-testid="edit-pack-credits"
                                    />
                                  </div>
                                  <div>
                                    <Label>Price (NGN)</Label>
                                    <Input 
                                      type="number"
                                      value={editingPack.price_ngn}
                                      onChange={(e) => setEditingPack({...editingPack, price_ngn: parseInt(e.target.value) || 0})}
                                      data-testid="edit-pack-ngn"
                                    />
                                  </div>
                                  <div>
                                    <Label>Price (USD)</Label>
                                    <Input 
                                      type="number"
                                      value={editingPack.price_usd}
                                      onChange={(e) => setEditingPack({...editingPack, price_usd: parseInt(e.target.value) || 0})}
                                      data-testid="edit-pack-usd"
                                    />
                                  </div>
                                  <div className="flex items-center justify-between">
                                    <Label>Active</Label>
                                    <Switch 
                                      checked={editingPack.is_active}
                                      onCheckedChange={(v) => setEditingPack({...editingPack, is_active: v})}
                                      data-testid="edit-pack-active"
                                    />
                                  </div>
                                  <Button onClick={handleUpdatePricing} className="w-full" data-testid="save-pack-btn">
                                    Save Changes
                                  </Button>
                                </div>
                              )}
                            </DialogContent>
                          </Dialog>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}

          {/* ============== EMAIL CONFIG TAB ============== */}
          {activeTab === 'email' && (
            <Card className="card-elevated" data-testid="email-section">
              <CardHeader>
                <CardTitle className="text-foreground">Email Configuration</CardTitle>
                <CardDescription>SendPulse email service status</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className={`flex items-center gap-4 p-4 rounded-lg ${data.emailStatus?.email_configured ? 'bg-primary/5 border border-primary/20' : 'bg-destructive/5 border border-destructive/20'}`}>
                  {data.emailStatus?.email_configured ? (
                    <CheckCircle className="h-6 w-6 text-primary shrink-0" />
                  ) : (
                    <XCircle className="h-6 w-6 text-destructive shrink-0" />
                  )}
                  <div>
                    <p className="font-medium text-foreground">
                      {data.emailStatus?.email_configured ? 'Email Configured' : 'Email Not Configured'}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      {data.emailStatus?.email_configured 
                        ? 'Signup and verification emails are active.' 
                        : 'Signup is disabled. Configure the missing environment variables to enable.'}
                    </p>
                  </div>
                </div>

                <div className="space-y-3">
                  <h3 className="text-sm font-semibold text-foreground">Configuration Details</h3>
                  <Table>
                    <TableBody>
                      <TableRow>
                        <TableCell className="font-medium text-foreground">Provider</TableCell>
                        <TableCell>{data.emailStatus?.provider || 'SendPulse'}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell className="font-medium text-foreground">From Email</TableCell>
                        <TableCell>{data.emailStatus?.from_email || <span className="text-destructive">Not set</span>}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell className="font-medium text-foreground">From Name</TableCell>
                        <TableCell>{data.emailStatus?.from_name || <span className="text-destructive">Not set</span>}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell className="font-medium text-foreground">Signup Enabled</TableCell>
                        <TableCell>
                          {data.emailStatus?.signup_enabled ? (
                            <Badge className="bg-primary/10 text-primary border-0">Yes</Badge>
                          ) : (
                            <Badge className="bg-destructive/10 text-destructive border-0">No</Badge>
                          )}
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </div>

                {data.emailStatus?.missing_config?.length > 0 && (
                  <div className="space-y-3">
                    <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
                      <AlertTriangle className="h-4 w-4 text-destructive" />
                      Missing Environment Variables
                    </h3>
                    <div className="space-y-2">
                      {data.emailStatus.missing_config.map((varName) => (
                        <div key={varName} className="flex items-center gap-2 p-3 rounded-lg bg-destructive/5 border border-destructive/10">
                          <XCircle className="h-4 w-4 text-destructive shrink-0" />
                          <code className="text-sm font-mono text-foreground">{varName}</code>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* ============== SERVICES CONFIG TAB ============== */}
          {activeTab === 'services-config' && (
            <Card className="card-elevated" data-testid="services-config-section">
              <CardHeader>
                <CardTitle className="text-foreground">Service Configuration</CardTitle>
                <CardDescription>Per-service integration status</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {data.serviceConfig && Object.entries(data.serviceConfig).map(([key, svc]) => (
                  <div key={key} className={`flex items-center justify-between p-4 rounded-lg border ${svc.configured ? 'bg-primary/5 border-primary/20' : 'bg-destructive/5 border-destructive/20'}`}>
                    <div className="flex items-center gap-3">
                      {svc.configured ? (
                        <CheckCircle className="h-5 w-5 text-primary" />
                      ) : (
                        <XCircle className="h-5 w-5 text-destructive" />
                      )}
                      <div>
                        <p className="font-medium text-foreground capitalize">{key.replace(/_/g, ' ')}</p>
                        <p className="text-xs text-muted-foreground">Provider: {svc.provider}{svc.environment ? ` (${svc.environment})` : ''}</p>
                      </div>
                    </div>
                    <Badge className={`${svc.configured ? 'bg-primary/10 text-primary' : 'bg-destructive/10 text-destructive'} border-0`}>
                      {svc.configured ? 'Active' : 'Not Configured'}
                    </Badge>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}

          {/* ============== VISA PRESETS TAB ============== */}
          {activeTab === 'visa-presets' && (
            <Card className="card-elevated" data-testid="visa-presets-section">
              <CardHeader>
                <CardTitle className="text-foreground">Visa / Passport Photo Presets</CardTitle>
                <CardDescription>{data.visaPresets.length} presets loaded from database. Toggle to enable/disable for users.</CardDescription>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Country</TableHead>
                      <TableHead>Preset Name</TableHead>
                      <TableHead>Size (mm)</TableHead>
                      <TableHead>Pixels @300dpi</TableHead>
                      <TableHead>Background</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Action</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data.visaPresets.map(preset => (
                      <TableRow key={preset.id} data-testid={`preset-row-${preset.id}`}>
                        <TableCell>
                          <span className="font-medium">{preset.country_name}</span>
                          <span className="text-xs text-muted-foreground ml-1">({preset.country_code})</span>
                        </TableCell>
                        <TableCell className="text-sm">{preset.preset_name}</TableCell>
                        <TableCell className="text-sm">{preset.width_mm}x{preset.height_mm}</TableCell>
                        <TableCell className="text-sm">{preset.width_px_at_300dpi}x{preset.height_px_at_300dpi}</TableCell>
                        <TableCell className="text-sm capitalize">{preset.background_rule}</TableCell>
                        <TableCell>
                          <Badge className={`${preset.is_active ? 'bg-primary/10 text-primary' : 'bg-destructive/10 text-destructive'} border-0`}>
                            {preset.is_active ? 'Active' : 'Disabled'}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Switch
                            checked={preset.is_active}
                            onCheckedChange={() => handleTogglePreset(preset.id, preset.is_active)}
                            data-testid={`preset-toggle-${preset.id}`}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}

          {/* ============== SETTINGS TAB ============== */}
          {activeTab === 'settings' && (
            <Card className="card-elevated" data-testid="settings-section">
              <CardHeader>
                <CardTitle className="text-foreground">Account Settings</CardTitle>
                <CardDescription>Change your admin password</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="max-w-md space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="currentPassword">Current Password</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        id="currentPassword"
                        type="password"
                        placeholder="Enter current password"
                        value={passwordForm.current}
                        onChange={(e) => setPasswordForm({...passwordForm, current: e.target.value})}
                        className="pl-10"
                        data-testid="current-password-input"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="newPassword">New Password</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        id="newPassword"
                        type="password"
                        placeholder="Enter new password"
                        value={passwordForm.newPass}
                        onChange={(e) => setPasswordForm({...passwordForm, newPass: e.target.value})}
                        className="pl-10"
                        data-testid="new-password-input"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="confirmPassword">Confirm New Password</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        id="confirmPassword"
                        type="password"
                        placeholder="Confirm new password"
                        value={passwordForm.confirm}
                        onChange={(e) => setPasswordForm({...passwordForm, confirm: e.target.value})}
                        className="pl-10"
                        data-testid="confirm-password-input"
                      />
                    </div>
                  </div>
                  <Button 
                    onClick={handleChangePassword} 
                    disabled={passwordLoading}
                    className="w-full"
                    data-testid="change-password-btn"
                  >
                    {passwordLoading ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Changing...
                      </>
                    ) : (
                      'Change Password'
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </main>
    </div>
  );
}

